import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import type { PipelineItem } from "@/types/tina";

const STALE_HEARTBEAT_MS = 60_000;
const TYPICAL_STAGE_COUNT = 8;

interface Props {
  item: PipelineItem;
  stagesCount: number;
}

function secondsAgo(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 1000);
}

export function InProgressPanel({ item, stagesCount }: Props) {
  if (item.status === "pending") {
    return (
      <PanelShell
        status="pending"
        subtitle="Waiting to be claimed by a worker."
      >
        <p className="text-sm leading-relaxed">
          This item is queued. A worker will pick it up on the next claim cycle.
        </p>
      </PanelShell>
    );
  }

  if (item.status === "waiting_on_primary") {
    return (
      <PanelShell status="waiting_on_primary" subtitle="Waiting on primary">
        <p className="text-sm leading-relaxed">
          This follower depends on primary{" "}
          <span className="font-mono text-[13px]">
            {item.depends_on_event_id ?? "—"}
          </span>
          . Execution starts once the primary reaches a terminal state.
        </p>
      </PanelShell>
    );
  }

  const heartbeatSecs = secondsAgo(item.last_heartbeat_at);
  const stale =
    item.last_heartbeat_at !== null &&
    Date.now() - new Date(item.last_heartbeat_at).getTime() >
      STALE_HEARTBEAT_MS;
  const progress = Math.min(
    100,
    Math.round((stagesCount / TYPICAL_STAGE_COUNT) * 100),
  );

  return (
    <PanelShell
      status="in_progress"
      subtitle={`In stage ${item.current_stage ?? "—"} · attempt ${item.attempt_count} / 3`}
    >
      <div className="flex flex-col gap-1.5">
        <Progress value={progress} className="h-1.5" />
        <p className="text-xs text-muted-foreground tabular-nums">
          {stagesCount} stage execution{stagesCount === 1 ? "" : "s"} so far
        </p>
      </div>
      <KeyValueGrid
        rows={[
          {
            label: "Stage",
            value: item.current_stage ?? "—",
            mono: true,
          },
          {
            label: "Attempt",
            value: `${item.attempt_count} / 3`,
          },
          {
            label: "Claimed by",
            value: item.claimed_by ?? "—",
            mono: true,
          },
          {
            label: "Last heartbeat",
            value: (
              <span className="inline-flex items-center gap-2">
                <span className="tabular-nums">
                  {heartbeatSecs == null ? "—" : `${heartbeatSecs}s ago`}
                </span>
                {!stale && item.last_heartbeat_at && (
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                )}
              </span>
            ),
          },
        ]}
      />
      {stale && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Heartbeat is stale</AlertTitle>
          <AlertDescription>
            No heartbeat for over 60 seconds. The worker may have died — the run
            reaper will reclaim this item shortly.
          </AlertDescription>
        </Alert>
      )}
    </PanelShell>
  );
}
